'use client';
import React, {
  createContext,
  useContext,
  ReactNode,
  useMemo,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { useAuthStore } from '@/auth';
import { userServices } from 'src/services/api-services';

// Define the user status types
export type UserStatusType = 'guest' | 'logged' | 'subscribed' | 'loading';

/**
 * Interface defining course access permissions
 */
export interface CourseAccess {
  canAccessLockedContent: boolean;
  canAddFavorites: boolean;
  canWatchVideos: boolean;
  requiresAuth: boolean;
  requiresSubscription: boolean;
}

/**
 * Interface for the user status context value
 */
export interface UserStatusContextValue {
  // Status indicators
  userStatus: UserStatusType;
  isLoggedIn: boolean;
  isSubscribed: boolean;
  isLoadingSubscription: boolean;
  isInitializing: boolean;
  user: any; // User object from auth store

  // Permission helpers
  canAccessContent: (isLocked?: boolean) => boolean;
  canAddFavorites: () => boolean;
  canWatchVideos: () => boolean;

  // Action helpers
  getRedirectUrlForAction: (actionType: 'watch' | 'favorite', originalUrl?: string) => string;
  getCourseAccess: (isLocked?: boolean) => CourseAccess;

  // Redirect helpers
  handleAuthRedirect: (
    router: any,
    loginRedirectPath: string,
    subscriptionRedirectPath: string,
    skipRedirect?: boolean,
    isSeriesPurchased?: boolean,
  ) => void;
  shouldSkipRedirect: () => boolean;
}

// Create the context with a default value of undefined
export const UserStatusContext = createContext<UserStatusContextValue | undefined>(undefined);

// Props for the provider component
interface UserStatusProviderProps {
  children: ReactNode;
}

/**
 * Provider component that makes user status context available to all child components.
 */
export const UserStatusProvider: React.FC<UserStatusProviderProps> = ({ children }) => {
  const { user } = useAuthStore();
  const [isSubscribed, setIsSubscribed] = useState<boolean>(false);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  // Fetch subscription status when user is available
  useEffect(() => {
    let isMounted = true;

    const fetchSubscriptionStatus = async () => {
      if (!user || !user.token) {
        if (isMounted) {
          setIsSubscribed(false);
          setIsInitializing(false);
        }
        return;
      }

      setIsLoadingSubscription(true);
      try {
        const result = await userServices.getSubscriptionStatus(user.email, user.token || '');

        if (isMounted) {
          if (result.success && result.data) {
            setIsSubscribed(result.data.data.isSubscribed);
          } else {
            // Explicitly set to false if request was successful but user is not subscribed
            setIsSubscribed(false);
          }
        }
      } catch (error) {
        console.error('Failed to fetch subscription status:', error);
        if (isMounted) {
          // Set to false on error
          setIsSubscribed(false);
        }
      } finally {
        if (isMounted) {
          setIsLoadingSubscription(false);
          setIsInitializing(false);
        }
      }
    };

    // Set a reasonable delay for initialization to avoid flash redirects on page refresh
    const initTimer = setTimeout(() => {
      fetchSubscriptionStatus();
    }, 300);

    return () => {
      isMounted = false;
      clearTimeout(initTimer);
    };
  }, [user]);

  // Memoize the shouldSkipRedirect function
  const shouldSkipRedirect = useCallback(() => {
    // Skip redirects while initializing or loading subscription
    return isInitializing || isLoadingSubscription;
  }, [isInitializing, isLoadingSubscription]);

  // Memoize the handleAuthRedirect function
  const handleAuthRedirect = useCallback(
    (
      router: any,
      loginRedirectPath: string,
      subscriptionRedirectPath: string,
      skipRedirect: boolean = false,
      isSeriesPurchased: boolean = false,
    ) => {
      // If skipRedirect is true or we should skip redirects due to initialization, return early
      if (skipRedirect || isInitializing || isLoadingSubscription) {
        console.log('Skipping redirect - Still initializing or loading');
        return;
      }

      const isLoggedIn = !!user;

      // Handle redirects based on auth status
      if (!isLoggedIn) {
        console.log('Redirecting to login:', loginRedirectPath);
        router.push(loginRedirectPath);
      } else if (isLoggedIn && isSubscribed === false && isSeriesPurchased === false) {
        console.log('Redirecting to subscription page:', subscriptionRedirectPath);
        router.push(subscriptionRedirectPath);
      }
    },
    [user, isSubscribed, isInitializing, isLoadingSubscription],
  );

  // Compute the value for the context
  const contextValue = useMemo(() => {
    const isLoggedIn = !!user;

    // Determine user status
    let userStatus: UserStatusType = 'guest';
    if (isInitializing) {
      userStatus = 'loading';
    } else if (isLoggedIn) {
      userStatus = isSubscribed ? 'subscribed' : 'logged';
    }

    // Helper methods for permission checks
    const canAccessContent = (isLocked = false) => {
      // If content is not locked, anyone can access
      if (!isLocked) return true;
      // If content is locked, only subscribers can access
      return userStatus === 'subscribed';
    };

    const canAddFavorites = () => isLoggedIn;

    const canWatchVideos = () => isLoggedIn;

    // Helper for getting redirect URLs
    const getRedirectUrlForAction = (actionType: 'watch' | 'favorite', originalUrl = '') => {
      if (!isLoggedIn) {
        // Redirect to login page
        return `/auth/login?redirectTo=${encodeURIComponent(originalUrl)}`;
      }

      if (actionType === 'watch' && !canWatchVideos()) {
        // Redirect to subscription page
        return '/subscription';
      }

      if (actionType === 'favorite' && !canAddFavorites()) {
        // Redirect to subscription page
        return '/subscription';
      }

      // User has permission, return original URL
      return originalUrl;
    };

    // Get full access information for a course
    const getCourseAccess = (isLocked = false): CourseAccess => {
      return {
        canAccessLockedContent: userStatus === 'subscribed',
        canAddFavorites: isLoggedIn,
        canWatchVideos: isLoggedIn,
        requiresAuth: isLocked || false,
        requiresSubscription: isLocked,
      };
    };

    return {
      userStatus,
      isLoggedIn,
      isSubscribed,
      isLoadingSubscription,
      isInitializing,
      user,
      canAccessContent,
      canAddFavorites,
      canWatchVideos,
      getRedirectUrlForAction,
      getCourseAccess,
      handleAuthRedirect,
      shouldSkipRedirect,
    };
  }, [
    user,
    isSubscribed,
    isLoadingSubscription,
    isInitializing,
    handleAuthRedirect,
    shouldSkipRedirect,
  ]);

  return <UserStatusContext.Provider value={contextValue}>{children}</UserStatusContext.Provider>;
};

/**
 * Hook to use the user status context.
 * Must be used within a UserStatusProvider.
 */
export const useUserStatus = (): UserStatusContextValue => {
  const context = useContext(UserStatusContext);
  if (context === undefined) {
    throw new Error('useUserStatus must be used within a UserStatusProvider');
  }
  return context;
};
